/**
 * WalkInModal Component
 * Modal for creating walk-in appointments quickly
 * 
 * Features:
 * - Quick client creation/selection
 * - Service selection
 * - Professional assignment
 * - Creates appointment group and adds to Walk-in column
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  User, 
  Search, 
  Plus,
  Phone,
  Mail,
  Scissors,
  Clock,
  DollarSign,
  Minus,
  ShoppingCart,
  Check
} from 'lucide-react';

import { getCategories, getServicesByCategory } from '../services/categoryService';
import { getProfessionalsForService } from '../services/professionalService';
import { createWalkInAppointment, addServicesToAppointmentGroup } from '../services/appointmentService';
import { getClients } from '../services/clientService';

// Brazilian formatting utilities
const BRAZILIAN_STATES = [
  { code: 'AC', name: 'Acre' },
  { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' },
  { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' },
  { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' },
  { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' },
  { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' },
  { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' },
  { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' },
  { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' },
  { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' },
  { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' },
  { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' },
  { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' },
  { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' }
];

const formatCpf = (cpf) => {
  if (!cpf) return '';
  const numbers = cpf.replace(/\D/g, '');
  if (numbers.length <= 11) {
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return numbers.slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatCep = (cep) => {
  if (!cep) return '';
  const numbers = cep.replace(/\D/g, '');
  if (numbers.length <= 8) {
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
  return numbers.slice(0, 8).replace(/(\d{5})(\d{3})/, '$1-$2');
};

const lookupCep = async (cep) => {
  const cleanCep = cep.replace(/\D/g, '');
  if (cleanCep.length !== 8) return null;
  
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
    const data = await response.json();
    
    if (data.erro) return null;
    
    return {
      address_street: data.logradouro || '',
      address_neighborhood: data.bairro || '',
      address_city: data.localidade || '',
      address_state: data.uf || '',
      address_complement: data.complemento || ''
    };
  } catch (error) {
    return null;
  }
};

const WalkInModal = ({ 
  isOpen, 
  onClose, 
  onWalkInCreated,
  preloadedServices,
  modalContext 
}) => {
  // State management
  const [step, setStep] = useState(1); // 1: Client, 2: Services, 3: Professional
  const [clientData, setClientData] = useState({
    id: null,
    name: '',
    nickname: '',
    phone: '',
    email: '',
    cpf: '',
    address_cep: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    isNewClient: false
  });
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedProfessional, setProfessional] = useState(null);
  const [serviceAssignments, setServiceAssignments] = useState({}); // { serviceId: professionalId }
  const [estimatedDuration, setEstimatedDuration] = useState(0);
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  
  // Data loading states
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [professionalsByService, setProfessionalsByService] = useState({}); // { serviceId: [professionals] }
  const [isLoading, setIsLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [showClientSearch, setShowClientSearch] = useState(true);
  const [showMoreFields, setShowMoreFields] = useState(false);
  
  // Refs
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  
  // Focus management
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [isOpen, step]);
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  
  // Load initial data when modal opens (step 1) so services are ready when user reaches step 2
  useEffect(() => {
    if (isOpen && step === 1) {
      loadCategories();
    }
  }, [isOpen, step]);

  // Handle modal context (auto-select client for checkout context)
  useEffect(() => {
    if (isOpen) {
      if (modalContext?.mode === 'add-to-existing' && modalContext.existingClient) {
        // Auto-select existing client
        setClientData({
          id: modalContext.existingClient.id,
          name: modalContext.existingClient.name || 'Cliente Existente',
          email: '', // We'll load this if needed
          phone: '',  // We'll load this if needed
          isNewClient: false
        });
        
        // Skip to step 2 (services) since client is already selected
        setStep(2);
      } else {
        // Reset for new appointment
        setStep(1);
        setClientData({ id: null, name: '', email: '', phone: '', isNewClient: false });
        setSelectedServices([]);
        setServiceAssignments({});
        setShowClientSearch(true);
      }
    }
  }, [isOpen, modalContext]);
  
  const loadCategories = async () => {
    try {
      setLoadingServices(true);
      
      // Use preloaded services if available
      if (preloadedServices) {
        setCategories(preloadedServices.categories);
        setServices(preloadedServices.services);
        // No loading delay since data is already available
        setLoadingServices(false);
        return;
      }
      
      
      // Fallback to API call if preloaded services not available
      const data = await getCategories();
      setCategories(data);
      
      // Load all services from all categories for search
      const allServices = [];
      for (const category of data) {
        const categoryServices = await getServicesByCategory(category.id);
        allServices.push(...categoryServices.map(service => ({ ...service, category_name: category.name })));
      }
      setServices(allServices);
    } catch (err) {
      setError('Failed to load services');
    } finally {
      setLoadingServices(false);
    }
  };
  
  // Filter services based on search
  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(serviceSearchQuery.toLowerCase()) ||
    service.category_name.toLowerCase().includes(serviceSearchQuery.toLowerCase()) ||
    service.service_sku?.toLowerCase().includes(serviceSearchQuery.toLowerCase())
  );
  
  // Handle service selection
  const toggleService = (service) => {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === service.id);
      if (exists) {
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
  };
  
  // Calculate totals when services change
  useEffect(() => {
    const duration = selectedServices.reduce((sum, service) => sum + (service.duration_minutes || 0), 0);
    const price = selectedServices.reduce((sum, service) => sum + parseFloat(service.price || 0), 0);
    setEstimatedDuration(duration);
    setEstimatedPrice(price);
  }, [selectedServices]);
  
  // Load professionals when services are selected
  useEffect(() => {
    if (selectedServices.length > 0) {
      loadProfessionalsForServices();
    }
  }, [selectedServices]);
  
  const loadProfessionalsForServices = async () => {
    try {
      setLoadingProfessionals(true);
      // Get all professionals for each service (for individual assignment)
      const professionalSets = await Promise.all(
        selectedServices.map(async (service) => {
          const serviceProfessionals = await getProfessionalsForService(service.id);
          return {
            serviceId: service.id,
            serviceName: service.name,
            professionals: serviceProfessionals
          };
        })
      );
      
      // Create a map of serviceId -> available professionals
      const servicesProfessionalsMap = {};
      professionalSets.forEach(({ serviceId, professionals }) => {
        servicesProfessionalsMap[serviceId] = professionals;
      });
      
      // For backward compatibility, also set a unified list of all unique professionals
      const allProfessionals = [];
      const professionalIds = new Set();
      professionalSets.forEach(({ professionals }) => {
        professionals.forEach(prof => {
          if (!professionalIds.has(prof.id)) {
            professionalIds.add(prof.id);
            allProfessionals.push(prof);
          }
        });
      });
      
      setProfessionals(allProfessionals);
      
      // Store the service-specific professionals mapping
      setProfessionalsByService(servicesProfessionalsMap);
      
    } catch (err) {
      setError('Failed to load professionals');
      setProfessionals([]);
      setProfessionalsByService({});
    } finally {
      setLoadingProfessionals(false);
    }
  };

  // Search for existing clients - optimized with 3 char minimum
  const handleClientSearch = async (query) => {
    setClientSearchQuery(query);
    
    if (query.length < 3) {
      setClientSearchResults([]);
      return;
    }

    try {
      setSearchingClients(true);
      const response = await getClients({ search: query, limit: 10000 });
      const allClients = response.items || [];
      
      // WORKAROUND: Backend search is not working, filter client-side
      if (allClients.length > 0) {
        const searchLower = query.toLowerCase();
        const filtered = allClients.filter(client => {
          const name = (client.full_name || client.name || '').toLowerCase();
          const nickname = (client.nickname || '').toLowerCase();
          const email = (client.email || '').toLowerCase();
          const phone = (client.phone_number || client.phone || '').replace(/\D/g, '');
          const searchDigits = query.replace(/\D/g, '');
          
          return name.includes(searchLower) || 
                 nickname.includes(searchLower) ||
                 email.includes(searchLower) ||
                 (searchDigits && phone.includes(searchDigits));
        });
        
        setClientSearchResults(filtered.slice(0, 20)); // Show top 20 matches
      } else {
        setClientSearchResults([]);
      }
    } catch (err) {
      setClientSearchResults([]);
    } finally {
      setSearchingClients(false);
    }
  };

  // Select existing client from search results
  const selectExistingClient = (client) => {
    setClientData({
      id: client.id,
      name: client.full_name || client.name,
      phone: client.phone || '',
      email: client.email || '',
      isNewClient: false
    });
    setShowClientSearch(false);
    setClientSearchQuery('');
    setClientSearchResults([]);
  };

  // Toggle between new client and existing client modes
  const toggleClientMode = () => {
    if (showClientSearch) {
      // Switch to new client mode
      setShowClientSearch(false);
      setClientData({ 
        id: null, 
        name: '', 
        nickname: '',
        phone: '', 
        email: '', 
        cpf: '',
        address_cep: '',
        address_street: '',
        address_number: '',
        address_complement: '',
        address_neighborhood: '',
        address_city: '',
        address_state: '',
        isNewClient: true 
      });
      setClientSearchQuery('');
      setClientSearchResults([]);
    } else {
      // Switch to existing client search mode
      setShowClientSearch(true);
      setClientData({ 
        id: null, 
        name: '', 
        nickname: '',
        phone: '', 
        email: '', 
        cpf: '',
        address_cep: '',
        address_street: '',
        address_number: '',
        address_complement: '',
        address_neighborhood: '',
        address_city: '',
        address_state: '',
        isNewClient: false 
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if we're adding to existing appointment group
      if (modalContext?.mode === 'add-to-existing') {
        // Add services to existing group
        const servicesToAdd = selectedServices.map(s => ({ 
          id: s.id,
          professional_id: serviceAssignments[s.id]
        }));
        
        const response = await addServicesToAppointmentGroup(
          modalContext.existingGroupId, 
          servicesToAdd
        );
        
        // Extract appointment group from response
        onWalkInCreated(response.appointment_group); // Notify parent to refresh data
        onClose();
        resetForm();
        return;
      } else {
        // Validate client data before submission
        if (clientData.isNewClient && !clientData.name?.trim()) {
          setError('Nome do cliente é obrigatório');
          return;
        }
        
        // Create new appointment group (original behavior)
        const walkInData = {
          client: clientData.isNewClient ? {
            name: clientData.name.trim(),
            nickname: clientData.nickname?.trim() || null,
            phone: clientData.phone || null,
            email: clientData.email || null,
            cpf: clientData.cpf || null,
            address_cep: clientData.address_cep || null,
            address_street: clientData.address_street?.trim() || null,
            address_number: clientData.address_number?.trim() || null,
            address_complement: clientData.address_complement?.trim() || null,
            address_neighborhood: clientData.address_neighborhood?.trim() || null,
            address_city: clientData.address_city?.trim() || null,
            address_state: clientData.address_state || null
          } : {
            id: clientData.id
          },
          services: selectedServices.map(s => ({ 
            id: s.id,
            professional_id: serviceAssignments[s.id]
          }))
        };
        
        const result = await createWalkInAppointment(walkInData);
        onWalkInCreated(result);
        onClose();
        resetForm();
      }
      
    } catch (err) {
      setError('Erro ao criar serviços');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setStep(1);
    setClientData({ 
      id: null, 
      name: '', 
      nickname: '',
      phone: '', 
      email: '', 
      cpf: '',
      address_cep: '',
      address_street: '',
      address_number: '',
      address_complement: '',
      address_neighborhood: '',
      address_city: '',
      address_state: '',
      isNewClient: false 
    });
    setSelectedServices([]);
    setProfessional(null);
    setServiceAssignments({});
    setProfessionalsByService({});
    setServiceSearchQuery('');
    setClientSearchQuery('');
    setClientSearchResults([]);
    setShowClientSearch(true);
    setShowMoreFields(false);
    setError(null);
  };
  
  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };
  
  // Format duration
  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : ''}`.trim() || '0min';
  };
  
  // Check if all services have been assigned to professionals
  const areAllServicesAssigned = () => {
    return selectedServices.every(service => serviceAssignments[service.id]);
  };

  // Handle CEP lookup
  const handleCepLookup = async (cep) => {
    const addressData = await lookupCep(cep);
    if (addressData) {
      setClientData(prev => ({
        ...prev,
        address_street: addressData.address_street || prev.address_street,
        address_neighborhood: addressData.address_neighborhood || prev.address_neighborhood,
        address_city: addressData.address_city || prev.address_city,
        address_state: addressData.address_state || prev.address_state,
        address_complement: addressData.address_complement || prev.address_complement
      }));
    }
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide uppercase">Informações do Cliente</h3>
              <button
                onClick={toggleClientMode}
                className="text-xs text-brand-dark dark:text-brand-light font-medium uppercase tracking-widest hover:opacity-70 transition-all duration-300 border border-brand-dark/20 dark:border-brand-light/20 px-3 py-2 touch-manipulation"
              >
                {showClientSearch ? 'Novo Cliente' : 'Cliente Existente'}
              </button>
            </div>

            {showClientSearch ? (
              /* Existing client search mode */
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-3 uppercase tracking-wider">
                    <Search size={14} className="inline mr-2" strokeWidth={1.5} />
                    Buscar Cliente Existente
                  </label>
                  <input
                    ref={firstInputRef}
                    type="text"
                    value={clientSearchQuery}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    className="w-full px-4 py-4 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                    placeholder="Digite pelo menos 3 caracteres..."
                    inputMode="search"
                  />
                </div>

                {/* Search results */}
                {clientSearchQuery.length >= 3 && (
                  <div className="max-h-64 overflow-y-auto">
                    {searchingClients ? (
                      <div className="text-center py-4">
                        <div className="w-6 h-6 border-2 border-brand-dark dark:border-brand-light border-t-transparent mx-auto mb-2 animate-spin"></div>
                        <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">Buscando clientes...</p>
                      </div>
                    ) : clientSearchResults.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 mb-2 uppercase tracking-wider">
                          {clientSearchResults.length} cliente{clientSearchResults.length !== 1 ? 's' : ''} encontrado{clientSearchResults.length !== 1 ? 's' : ''}:
                        </p>
                        {clientSearchResults.map((client) => (
                          <div
                            key={client.id}
                            onClick={() => selectExistingClient(client)}
                            className="p-4 border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 cursor-pointer transition-all duration-300 touch-manipulation"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-brand-dark/10 dark:bg-brand-light/10 border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center">
                                <User size={18} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-brand-dark dark:text-brand-light truncate tracking-wide">
                                  {client.full_name || client.name}
                                </h4>
                                <div className="text-sm text-brand-dark/60 dark:text-brand-light/60 space-y-1 tracking-wide">
                                  {client.phone && <p>{client.phone}</p>}
                                  {client.email && <p>{client.email}</p>}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-brand-dark/60 dark:text-brand-light/60">
                        <User size={32} className="mx-auto mb-2 text-brand-dark/20 dark:text-brand-light/20" strokeWidth={1.5} />
                        <p className="text-xs uppercase tracking-wider">Nenhum cliente encontrado</p>
                        <button
                          onClick={toggleClientMode}
                          className="text-brand-dark dark:text-brand-light text-xs font-medium mt-2 uppercase tracking-widest border-b border-brand-dark/30 dark:border-brand-light/30 hover:border-brand-dark dark:hover:border-brand-light transition-all duration-300 touch-manipulation"
                        >
                          Criar novo cliente
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Selected client display */}
                {clientData.id && !clientData.isNewClient && (
                  <div className="p-4 border border-green-600/20 dark:border-green-400/20 bg-transparent">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-green-600 dark:text-green-400 uppercase tracking-wide text-xs">Cliente Selecionado</h4>
                        <p className="text-green-600 dark:text-green-400 font-serif tracking-wide">{clientData.name}</p>
                        {clientData.phone && <p className="text-sm text-green-600/80 dark:text-green-400/80 tracking-wide">{clientData.phone}</p>}
                        {clientData.email && <p className="text-sm text-green-600/80 dark:text-green-400/80 tracking-wide">{clientData.email}</p>}
                      </div>
                      <button
                        onClick={() => setClientData({ id: null, name: '', phone: '', email: '', isNewClient: false })}
                        className="text-green-600 dark:text-green-400 hover:opacity-70 transition-all duration-300 touch-manipulation"
                      >
                        <X size={20} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* New client creation mode */
              <div className="space-y-4">
                {/* Essential Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                      Nome do Cliente *
                    </label>
                    <input
                      ref={firstInputRef}
                      type="text"
                      value={clientData.name}
                      onChange={(e) => setClientData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                      placeholder="Digite o nome do cliente"
                      required
                      autoComplete="name"
                    />
                  </div>
                  
                  {/* Nickname and Phone in row for space efficiency */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                        Apelido
                      </label>
                      <input
                        type="text"
                        value={clientData.nickname}
                        onChange={(e) => setClientData(prev => ({ ...prev, nickname: e.target.value }))}
                        className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                        placeholder="Apelido (opcional)"
                        autoComplete="nickname"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                        <Phone size={14} className="inline mr-1" strokeWidth={1.5} />
                        Telefone
                      </label>
                      <input
                        type="tel"
                        value={clientData.phone}
                        onChange={(e) => setClientData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                        placeholder="(11) 99999-9999"
                        autoComplete="tel"
                        inputMode="tel"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                      <Mail size={14} className="inline mr-1" strokeWidth={1.5} />
                      Email
                    </label>
                    <input
                      type="email"
                      value={clientData.email}
                      onChange={(e) => setClientData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                      placeholder="cliente@exemplo.com"
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>
                </div>

                {/* More Fields Toggle */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreFields(!showMoreFields);
                    }}
                    className="text-brand-dark dark:text-brand-light font-medium text-xs py-2 px-4 border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300 uppercase tracking-wider touch-manipulation"
                  >
                    {showMoreFields ? 'Ocultar campos opcionais' : 'Adicionar CPF e endereço'}
                    <span className="ml-1">{showMoreFields ? '▲' : '▼'}</span>
                  </button>
                </div>

                {/* Additional Fields - Collapsible */}
                {showMoreFields && (
                  <div className="space-y-4 pt-2 border-t border-brand-dark/10 dark:border-brand-light/10">
                    {/* CPF */}
                    <div>
                      <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                        CPF
                      </label>
                      <input
                        type="text"
                        value={clientData.cpf}
                        onChange={(e) => setClientData(prev => ({ ...prev, cpf: formatCpf(e.target.value) }))}
                        className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                        placeholder="000.000.000-00"
                        maxLength={14}
                        inputMode="numeric"
                      />
                    </div>

                    {/* Address Section Header */}
                    <div className="flex items-center gap-2 pt-2">
                      <div className="h-px bg-brand-dark/10 dark:bg-brand-light/10 flex-1"></div>
                      <span className="text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 px-2 uppercase tracking-wider">ENDEREÇO</span>
                      <div className="h-px bg-brand-dark/10 dark:bg-brand-light/10 flex-1"></div>
                    </div>

                    {/* CEP with auto-fill */}
                    <div>
                      <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                        CEP
                      </label>
                      <input
                        type="text"
                        value={clientData.address_cep}
                        onChange={(e) => setClientData(prev => ({ ...prev, address_cep: formatCep(e.target.value) }))}
                        onBlur={(e) => {
                          if (e.target.value.replace(/\D/g, '').length === 8) {
                            handleCepLookup(e.target.value);
                          }
                        }}
                        className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                        placeholder="00000-000"
                        maxLength={9}
                        inputMode="numeric"
                      />
                      <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 mt-1 tracking-wide">Será preenchido automaticamente</p>
                    </div>

                    {/* Street and Number in compact row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                          Rua
                        </label>
                        <input
                          type="text"
                          value={clientData.address_street}
                          onChange={(e) => setClientData(prev => ({ ...prev, address_street: e.target.value }))}
                          className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                          placeholder="Nome da rua"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                          Nº
                        </label>
                        <input
                          type="text"
                          value={clientData.address_number}
                          onChange={(e) => setClientData(prev => ({ ...prev, address_number: e.target.value }))}
                          className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                          placeholder="123"
                          inputMode="numeric"
                        />
                      </div>
                    </div>

                    {/* Complement and Neighborhood */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                          Complemento
                        </label>
                        <input
                          type="text"
                          value={clientData.address_complement}
                          onChange={(e) => setClientData(prev => ({ ...prev, address_complement: e.target.value }))}
                          className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                          placeholder="Apto, Bloco, etc."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                          Bairro
                        </label>
                        <input
                          type="text"
                          value={clientData.address_neighborhood}
                          onChange={(e) => setClientData(prev => ({ ...prev, address_neighborhood: e.target.value }))}
                          className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                          placeholder="Nome do bairro"
                        />
                      </div>
                    </div>

                    {/* City and State */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                          Cidade
                        </label>
                        <input
                          type="text"
                          value={clientData.address_city}
                          onChange={(e) => setClientData(prev => ({ ...prev, address_city: e.target.value }))}
                          className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                          placeholder="Cidade"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                          Estado
                        </label>
                        <select
                          value={clientData.address_state}
                          onChange={(e) => setClientData(prev => ({ ...prev, address_state: e.target.value }))}
                          className="w-full px-4 py-3 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                        >
                          <option value="">UF</option>
                          {BRAZILIAN_STATES.map(state => (
                            <option key={state.code} value={state.code}>
                              {state.code}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-base font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide uppercase">Selecionar Serviços</h3>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" size={18} strokeWidth={1.5} />
              <input
                ref={firstInputRef}
                type="text"
                value={serviceSearchQuery}
                onChange={(e) => setServiceSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-base border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                placeholder="Buscar serviços..."
                inputMode="search"
              />
            </div>
            
            {/* Shopping Cart Summary - Luxury Design */}
            {selectedServices.length > 0 && (
              <div className="sticky top-0 z-10 mb-4 p-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light/50 dark:bg-brand-dark/50 backdrop-blur-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <ShoppingCart size={14} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
                    <span className="text-xs text-brand-dark dark:text-brand-light font-medium uppercase tracking-wider">
                      {selectedServices.length} serviço{selectedServices.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                      {formatPrice(estimatedPrice)}
                    </span>
                    <button
                      onClick={() => setSelectedServices([])}
                      className="text-xs text-brand-dark dark:text-brand-light font-medium border border-brand-dark/20 dark:border-brand-light/20 px-2 py-1 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300 uppercase tracking-wider touch-manipulation"
                    >
                      Limpar
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Services list */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {loadingServices ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent mx-auto mb-4 animate-spin"></div>
                  <p className="text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider text-xs">Carregando serviços...</p>
                </div>
              ) : filteredServices.map((service) => {
                const isSelected = selectedServices.some(s => s.id === service.id);
                return (
                  <div
                    key={service.id}
                    onClick={() => toggleService(service)}
                    className={`p-4 border cursor-pointer transition-all touch-manipulation ${
                      isSelected
                        ? 'border-brand-dark dark:border-brand-light bg-brand-dark/5 dark:bg-brand-light/5'
                        : 'border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-brand-dark dark:text-brand-light mb-1 leading-snug tracking-wide">{service.name}</h4>
                        <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 mb-2 uppercase tracking-wider">{service.category_name}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-brand-dark/60 dark:text-brand-light/60 flex items-center tracking-wide">
                            <Clock size={12} className="mr-1.5" strokeWidth={1.5} />
                            {formatDuration(service.duration_minutes)}
                          </span>
                          <span className="text-xs text-green-600 dark:text-green-400 font-serif font-bold flex items-center tracking-wide">
                            <DollarSign size={12} className="mr-1" strokeWidth={1.5} />
                            {formatPrice(service.price)}
                          </span>
                        </div>
                      </div>
                      <div className={`w-7 h-7 border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${
                        isSelected ? 'bg-brand-dark dark:bg-brand-light border-brand-dark dark:border-brand-light' : 'border-brand-dark/30 dark:border-brand-light/30'
                      }`}>
                        {isSelected ? (
                          <Check size={16} className="text-brand-light dark:text-brand-dark" strokeWidth={2} />
                        ) : (
                          <Plus size={16} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {!loadingServices && filteredServices.length === 0 && (
                <div className="text-center py-12 text-brand-dark/60 dark:text-brand-light/60">
                  <Scissors size={32} className="mx-auto mb-3 text-brand-dark/20 dark:text-brand-light/20" strokeWidth={1.5} />
                  <p className="uppercase tracking-wider text-xs">Nenhum serviço encontrado</p>
                  <p className="text-xs mt-1 tracking-wide">Tente uma busca diferente</p>
                </div>
              )}
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-base font-serif font-bold text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wide">Atribuir Profissionais</h3>
              <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Selecione um profissional para cada serviço</p>
            </div>
            
            {selectedServices.length === 0 ? (
              <div className="text-center py-12 text-brand-dark/60 dark:text-brand-light/60">
                <User size={48} className="mx-auto mb-4 text-brand-dark/20 dark:text-brand-light/20" strokeWidth={1.5} />
                <p className="font-medium mb-2 uppercase tracking-wider text-xs">Nenhum serviço selecionado</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedServices.map((service) => {
                  const availableProfessionals = professionalsByService[service.id] || [];
                  const assignedProfessionalId = serviceAssignments[service.id];
                  const assignedProfessional = availableProfessionals.find(p => p.id === assignedProfessionalId);

                  return (
                    <div key={service.id} className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 overflow-hidden">
                      {/* Service Header */}
                      <div className="bg-brand-dark/5 dark:bg-brand-light/5 px-4 py-3 border-b border-brand-dark/10 dark:border-brand-light/10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-brand-dark/10 dark:bg-brand-light/10 border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center">
                              <Scissors size={18} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
                            </div>
                            <div>
                              <h4 className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{service.name}</h4>
                              <div className="flex items-center space-x-3 text-xs text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                                <span className="flex items-center">
                                  <Clock size={12} className="mr-1" strokeWidth={1.5} />
                                  {formatDuration(service.duration_minutes)}
                                </span>
                                <span className="flex items-center font-serif font-bold">
                                  <DollarSign size={12} className="mr-1" strokeWidth={1.5} />
                                  {formatPrice(service.price)}
                                </span>
                              </div>
                            </div>
                          </div>
                          {assignedProfessional && (
                            <div className="flex items-center space-x-2 border border-green-600/20 dark:border-green-400/20 px-3 py-1">
                              <Check size={12} className="text-green-600 dark:text-green-400" strokeWidth={1.5} />
                              <span className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">Atribuído</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Professionals List */}
                      <div className="p-4">
                        {availableProfessionals.length === 0 ? (
                          <div className="text-center py-6 text-brand-dark/60 dark:text-brand-light/60">
                            <User size={32} className="mx-auto mb-2 text-brand-dark/20 dark:text-brand-light/20" strokeWidth={1.5} />
                            <p className="text-xs uppercase tracking-wider">Nenhum profissional disponível para este serviço</p>
                            <p className="text-xs text-brand-dark/40 dark:text-brand-light/40 mt-1 tracking-wide">Serviço: {service.name}</p>
                          </div>
                        ) : (
                          <div className="mb-2">
                            <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                              {availableProfessionals.length} profissional{availableProfessionals.length !== 1 ? 'is' : ''} disponível{availableProfessionals.length !== 1 ? 'is' : ''} para {service.name}
                            </p>
                          </div>
                        )}
                        {availableProfessionals.length > 0 && (
                          <div className="space-y-2">
                            {availableProfessionals.map((professional) => {
                              const isSelected = serviceAssignments[service.id] === professional.id;

                              return (
                                <div
                                  key={professional.id}
                                  onClick={() => {
                                    setServiceAssignments(prev => ({
                                      ...prev,
                                      [service.id]: isSelected ? null : professional.id
                                    }));
                                  }}
                                  className={`p-3 border cursor-pointer transition-all touch-manipulation ${
                                    isSelected
                                      ? 'border-brand-dark dark:border-brand-light bg-brand-dark/5 dark:bg-brand-light/5'
                                      : 'border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-brand-dark/10 dark:bg-brand-light/10 border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center flex-shrink-0">
                                      <User size={18} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h5 className="font-medium text-brand-dark dark:text-brand-light text-sm tracking-wide">{professional.full_name}</h5>
                                    </div>
                                    <div className={`w-5 h-5 border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                                      isSelected ? 'bg-brand-dark dark:bg-brand-light border-brand-dark dark:border-brand-light' : 'border-brand-dark/30 dark:border-brand-light/30'
                                    }`}>
                                      {isSelected && (
                                        <Check size={12} className="text-brand-light dark:text-brand-dark" strokeWidth={2} />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
        
        
      default:
        return null;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
      {/* Full screen modal */}
      <div
        ref={modalRef}
        className="bg-brand-light dark:bg-brand-dark h-full w-full overflow-hidden flex flex-col"
      >
        {/* Header - Luxury Design */}
        <div className="flex items-center justify-between p-4 border-b border-brand-dark/10 dark:border-brand-light/10 bg-brand-dark dark:bg-brand-light">
          <div className="flex-1">
            <h2 className="text-xl font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide">Adicionar Serviços</h2>
            <p className="text-xs text-brand-light/70 dark:text-brand-dark/70 tracking-wider uppercase">Passo {step} de 3</p>
          </div>
          <button
            onClick={onClose}
            className="p-3 border border-brand-light/20 dark:border-brand-dark/20 hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300 touch-manipulation"
            aria-label="Fechar modal"
          >
            <X size={22} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
          </button>
        </div>
        
        {/* Progress bar - Luxury Design */}
        <div className="px-4 py-3 bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10">
          <div className="w-full bg-brand-dark/10 dark:bg-brand-light/10 h-2">
            <div
              className="bg-brand-dark dark:bg-brand-light h-2 transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">
            <span className={step >= 1 ? 'text-brand-dark dark:text-brand-light font-medium' : ''}>Cliente</span>
            <span className={step >= 2 ? 'text-brand-dark dark:text-brand-light font-medium' : ''}>Serviços</span>
            <span className={step >= 3 ? 'text-brand-dark dark:text-brand-light font-medium' : ''}>Profissional</span>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 pb-safe">
          {error && (
            <div className="mb-4 p-4 border border-red-600/20 dark:border-red-400/20 bg-transparent">
              <p className="text-red-600 dark:text-red-400 text-sm font-medium tracking-wide">{error}</p>
            </div>
          )}

          {renderStepContent()}
        </div>
        
        {/* Footer - Luxury Design */}
        <div className="border-t border-brand-dark/10 dark:border-brand-light/10 bg-brand-light dark:bg-brand-dark p-4 pb-safe">
          <div className="flex gap-3">
            <button
              onClick={step === 1 ? onClose : () => setStep(step - 1)}
              className="w-24 py-3 px-4 text-brand-dark dark:text-brand-light font-medium border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300 touch-manipulation flex-shrink-0 uppercase tracking-wider text-xs"
            >
              {step === 1 ? 'Cancelar' : 'Voltar'}
            </button>

            <button
              onClick={() => {
                if (step === 3) {
                  handleSubmit();
                } else {
                  setStep(step + 1);
                }
              }}
              disabled={
                (step === 1 && (showClientSearch ? !clientData.id : !clientData.name)) ||
                (step === 2 && selectedServices.length === 0) ||
                (step === 3 && !areAllServicesAssigned())
              }
              className="flex-1 py-3 px-6 bg-brand-dark dark:bg-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 disabled:bg-brand-dark/30 dark:disabled:bg-brand-light/30 disabled:cursor-not-allowed text-brand-light dark:text-brand-dark font-medium border border-brand-dark dark:border-brand-light transition-all duration-300 touch-manipulation uppercase tracking-widest text-xs"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-brand-light dark:border-brand-dark border-t-transparent animate-spin"></div>
                  Carregando...
                </div>
              ) : step === 3 ? 'Adicionar Serviços' : (
                step === 2 && selectedServices.length > 0 ? (
                  <div className="flex items-center justify-center gap-2">
                    <ShoppingCart size={14} strokeWidth={1.5} />
                    <span>Continuar ({selectedServices.length})</span>
                  </div>
                ) : 'Próximo'
              )}
            </button>
          </div>

          {/* Cart total preview in footer for step 2 */}
          {step === 2 && selectedServices.length > 0 && (
            <div className="mt-3 pt-3 border-t border-brand-dark/10 dark:border-brand-light/10">
              <div className="flex justify-between items-center text-xs">
                <span className="text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">Total do carrinho:</span>
                <div className="flex items-center gap-3">
                  <span className="text-brand-dark/60 dark:text-brand-light/60 flex items-center tracking-wide">
                    <Clock size={12} className="mr-1" strokeWidth={1.5} />
                    {formatDuration(estimatedDuration)}
                  </span>
                  <span className="font-serif font-bold text-brand-dark dark:text-brand-light text-base tracking-wide">
                    {formatPrice(estimatedPrice)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalkInModal;